/**
 * 네이버 지도 SDK 중앙 로더.
 *
 * 거리뷰(로드맵) 미표출 버그의 근본 수정:
 * vue-plugin-load-script 의 loadScript 는 maps.js 의 <script> onload 에서 resolve
 * 하지만, panorama 서브모듈(maps-panorama.js)은 maps.js 가 onload 된 "이후" 비동기로
 * fetch 되므로 `naver.maps.Panorama` 는 그보다 늦게 정의된다. 그래서 loadScript 의
 * resolve 직후 `new naver.maps.Panorama()` 를 생성하면 race window 에서 throw 한다
 * (지도는 maps.js core 에 있어 먼저 그려지고, 거리뷰만 조용히 실패). 빠른 개발
 * 머신은 race 를 이기고, 콜드 캐시/느린 네트워크 사용자만 진다.
 *
 * 이 로더는 maps.js 와 "요청한 서브모듈의 런타임 전역"이 둘 다 준비된 뒤에만
 * resolve 한다. 호출부(MapDetail 등)는 기존처럼 promise 를 await 한 뒤 loadMap()
 * 을 부르면 되고, 더는 Panorama 미정의 race 를 겪지 않는다.
 *
 * 핵심 로직(waitFor)은 의존성 주입(clock/loadScript/getMaps)으로 분리하여 DOM
 * 없이 node:test 로 단위 검증한다. (frontend/src/utils/naverMaps.test.mjs)
 */

const NAVER_MAPS_URL = 'https://oapi.map.naver.com/openapi/v3/maps.js';

// 호출부가 "필요로 하는 것"별 준비 판정.
//   panorama → 거리뷰 서브모듈(maps-panorama.js)이 정의하는 naver.maps.Panorama
//   core     → base SDK(maps.js core)의 naver.maps.Map. 지도만 쓰는 출력 페이지용.
// maps.js URL 은 항상 &submodules=panorama 로 고정한다(모든 지도 뷰가 같은 src →
// $loadScript dedup 유지, 기존 네트워크 동작과 동일). 'core' 는 적재된 panorama
// 서브모듈을 "기다리지 않고" base SDK 준비만으로 resolve → panorama 지연/실패가
// 지도·PDF 렌더를 막지 않는다(Codex P2 회귀 방지). 미지정/미지값은 core 로 폴백.
const FEATURE_READY = {
	panorama: maps => typeof maps.Panorama === 'function',
	core: maps => typeof maps.Map === 'function',
};

/**
 * probe() 가 truthy 를 반환할 때까지 interval 간격으로 폴링한다. timeout 초과 시
 * reject. clock(now/sleep) 주입으로 순수·테스트 가능하게 유지한다.
 *
 * @param {() => boolean} probe
 * @param {{ interval?: number, timeout?: number, sleep: (ms:number)=>Promise<void>, now: ()=>number }} opts
 */
export async function waitFor(probe, { interval = 50, timeout = 15000, sleep, now }) {
	const start = now();
	for (;;) {
		if (probe()) return;
		if (now() - start >= timeout) {
			throw new Error('naver maps submodule load timeout');
		}
		await sleep(interval);
	}
}

/**
 * 주입된 의존성에 묶인 로더를 생성한다. 반환값은 `loadNaverMaps(submodule)`.
 *
 * @param {Object} deps
 * @param {(src:string)=>Promise<unknown>} deps.loadScript  <script> onload 에서 resolve
 * @param {()=>(object|undefined)} deps.getMaps             window.naver.maps | undefined
 * @param {string} deps.clientId                            NCP keyId (VUE_APP_NAVER_API_ID)
 * @param {(ms:number)=>Promise<void>} [deps.sleep]         clock (기본 실타이머)
 * @param {()=>number} [deps.now]                           clock (기본 Date.now)
 * @param {number} [deps.timeout]                           준비 대기 한계(ms)
 * @returns {(feature?: 'panorama' | 'core') => Promise<object>}  거리뷰 뷰는 'panorama',
 *          지도만 쓰는 출력 페이지는 'core'. 반환값은 window.naver.maps.
 */
export function makeNaverMapsLoader({
	loadScript,
	getMaps,
	clientId,
	sleep = ms => new Promise(resolve => setTimeout(resolve, ms)),
	now = () => Date.now(),
	timeout = 15000,
}) {
	return async function loadNaverMaps(feature = 'panorama') {
		const isReady = FEATURE_READY[feature] || FEATURE_READY.core;
		const url = `${NAVER_MAPS_URL}?ncpKeyId=${clientId}&submodules=panorama`;
		await loadScript(url);
		await waitFor(
			() => {
				const maps = getMaps();
				return !!maps && isReady(maps);
			},
			{ sleep, now, timeout },
		);
		return getMaps();
	};
}
